import React, { useState, useEffect } from 'react';
import { formatUSD, formatDate, todayISO, generateTxnRef, COMPANY } from '../utils/keyGenerator';

const ISSUING_BANK = 'Dwin';
const ISSUING_BANK_BIC = 'BSAIMPM1XXX';

// Steps: 1=PAYOUT FORM, 2=REVIEW, 3=RESULT
export default function POBOSwift({ wallet, addTransaction, setSlip, accounts, preSelectedAccount, onAccountUsed }) {
  const [step, setStep] = useState(1);
  const [sourceAccountIban, setSourceAccountIban] = useState(preSelectedAccount?.iban || '');
  const [fiatWallet, setFiatWallet] = useState({ balance: 0, totalRedeemed: 0, totalPaidOut: 0, pendingPayouts: 0 });
  const [form, setForm] = useState({
    beneficiaryName: '', beneficiaryBank: '', beneficiaryBIC: '',
    beneficiaryAccount: '', currency: 'USD', amount: '',
    purposeOfPayment: '', purposeCode: 'OTHR', valueDate: todayISO(),
  });
  const [alert, setAlert] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [isoMessage, setIsoMessage] = useState('');
  const [payoutHistory, setPayoutHistory] = useState([]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Auto-select source account when navigating from Account page
  useEffect(() => {
    if (preSelectedAccount?.iban) {
      setSourceAccountIban(preSelectedAccount.iban);
      if (onAccountUsed) onAccountUsed();
    }
  }, [preSelectedAccount, onAccountUsed]);

  // Available balance = main wallet + fiat wallet
  const availableBalance = wallet.balance + fiatWallet.balance;

  // Load POBO wallet data on mount
  useEffect(() => {
    fetch('/api/pobo/wallet').then(r => { if (r.ok) return r.json(); throw new Error(); }).then(setFiatWallet).catch(() => {});
    fetch('/api/pobo/payouts').then(r => { if (r.ok) return r.json(); throw new Error(); }).then(setPayoutHistory).catch(() => {});
  }, [step]);

  // STEP 1→2: Review
  const handleGoToReview = () => {
    if (!form.beneficiaryName) { setAlert({ type: 'error', msg: 'Beneficiary name is required.' }); return; }
    if (!form.beneficiaryAccount) { setAlert({ type: 'error', msg: 'Beneficiary account/IBAN is required.' }); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { setAlert({ type: 'error', msg: 'Enter a valid amount.' }); return; }
    if (parseFloat(form.amount) > availableBalance) { setAlert({ type: 'error', msg: `Insufficient balance. Available: ${formatUSD(availableBalance)}` }); return; }
    setAlert(null);
    setStep(2);
  };

  // STEP 2→3: Submit POBO payout
  const handleSubmitPayout = async () => {
    setSending(true);
    const amt = parseFloat(form.amount);

    // If fiat wallet has insufficient balance, auto-redeem from main wallet first
    if (fiatWallet.balance < amt) {
      const redeemNeeded = amt - fiatWallet.balance;
      try {
        const redeemResp = await fetch('/api/pobo/redeem', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: redeemNeeded }),
        });
        const redeemData = await redeemResp.json();
        if (redeemData.success) {
          addTransaction({
            type: 'POBO', mt: 'REDEEM',
            debitAmount: redeemNeeded,
            description: `Auto-redeem to Fiat Wallet for SWIFT payout — ${redeemData.redemption.redeemId}`,
            counterparty: 'USDW Fiat Wallet', beneficiary: 'Fiat Wallet (Cash Store)',
            valueDate: todayISO(),
            currency: 'USD',
          });
          setFiatWallet(redeemData.wallet);
          // Sync RTGS balance
          try { await fetch('/api/rtgs/sync-balance', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ balance: wallet.balance - redeemNeeded }) }); } catch {}
        }
      } catch {}
    }

    // Now submit the SWIFT payout
    try {
      const resp = await fetch('/api/pobo/payout', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: amt, sourceAccountIban: sourceAccountIban || undefined }),
      });
      const data = await resp.json();
      setResult(data);
      if (data.isoMessage) setIsoMessage(data.isoMessage);
      if (data.success) {
        setFiatWallet(data.wallet);
        setSlip({
          method: `POBO SWIFT (${ISSUING_BANK})`,
          txnRef: data.payout.payoutId,
          valueDate: form.valueDate,
          beneficiary: form.beneficiaryName,
          accountNo: form.beneficiaryAccount,
          receiverBank: form.beneficiaryBank,
          currency: form.currency,
          amount: amt,
          kttEmail: 'bospnktt@bankopfsaipan.com',
          kttCode: '51 210619 BOSKTT G',
        });
        const emailStatus = data.emailSent ? ' | SWIFT email SENT to banking partner' : '';
        setAlert({ type: 'success', msg: `POBO SWIFT payout settled. UETR: ${data.payout.uetr}${emailStatus}` });
      } else {
        setAlert({ type: 'error', msg: data.error || 'Payout failed' });
      }
      setStep(3);
    } catch (err) {
      setAlert({ type: 'error', msg: `Error: ${err.message}` });
    }
    setSending(false);
  };

  // Reset
  const handleNew = () => {
    setForm({ beneficiaryName: '', beneficiaryBank: '', beneficiaryBIC: '', beneficiaryAccount: '', currency: 'USD', amount: '', purposeOfPayment: '', purposeCode: 'OTHR', valueDate: todayISO() });
    setResult(null); setIsoMessage(''); setAlert(null);
    setStep(1);
  };

  const reviewRows = [
    { section: 'POBO PARTY ROLES (ISO 20022)' },
    { label: 'Debtor (Sender of Record)', value: ISSUING_BANK, highlight: true },
    { label: 'Debtor Account', value: 'Issuing Bank Settlement Account' },
    { label: 'Ultimate Debtor', value: `${COMPANY} (USDW)`, gold: true },
    { label: 'Initiating Party', value: 'USDW Platform' },
    { label: 'Creditor (Beneficiary)', value: form.beneficiaryName },
    { section: 'PAYMENT DETAILS' },
    { label: 'Amount', value: formatUSD(parseFloat(form.amount || 0)), highlight: true },
    { label: 'Currency', value: form.currency },
    { label: 'Value Date', value: form.valueDate },
    { label: 'Beneficiary Account', value: form.beneficiaryAccount, mono: true },
    { label: 'Beneficiary Bank', value: form.beneficiaryBank || '-' },
    { label: 'Beneficiary BIC', value: form.beneficiaryBIC || '-', mono: true },
    { label: 'Purpose', value: form.purposeOfPayment || '-' },
    { label: 'Purpose Code', value: form.purposeCode },
    { section: 'EXECUTION PATH' },
    { label: 'SWIFT Message', value: 'pacs.008.001.10 (POBO)', mono: true },
    { label: 'SWIFT Sender', value: `${ISSUING_BANK} (${ISSUING_BANK_BIC})` },
    { label: 'Wallet Type', value: 'Sub-ledger (no bank account / no license)' },
    { label: 'Fiat Source', value: 'USDW Redemption (burned on-chain)' },
  ];

  return (
    <div className="gap-16">
      {alert && <div className={`alert alert-${alert.type}`}>{alert.type === 'success' ? '\u2713' : '\u2717'} {alert.msg}</div>}

      {/* Step indicator */}
      <div style={{ display: 'flex', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {[
          { n: 1, label: 'SWIFT Payout Details' },
          { n: 2, label: 'Review & Confirm' },
          { n: 3, label: 'Result' },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: '14px 16px', textAlign: 'center',
            background: step >= s.n ? (step === s.n ? 'var(--gold-soft)' : 'var(--surface2)') : 'var(--surface)',
            borderRight: s.n < 3 ? '1px solid var(--border)' : 'none',
            fontWeight: step === s.n ? 700 : 500, color: step >= s.n ? 'var(--gold)' : 'var(--text3)', fontSize: 13,
          }}>
            <span style={{ marginRight: 6, fontWeight: 700 }}>{s.n}</span>{s.label}
          </div>
        ))}
      </div>

      {/* STEP 1: PAYOUT FORM */}
      {step === 1 && (
        <div className="grid2">
          <div className="gap-16">
            {/* Balance Summary */}
            <div className="card" style={{ background: 'var(--surface2)' }}>
              <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, letterSpacing: '0.1em' }}>AVAILABLE BALANCE: </span>
                  <span style={{ fontSize: 16, color: 'var(--teal)', fontWeight: 700 }}>{formatUSD(availableBalance)}</span>
                </div>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>SWIFT via {ISSUING_BANK} ({ISSUING_BANK_BIC})</span>
              </div>
            </div>

            {/* Payout Form */}
            <div className="card">
              <div className="card-header"><span className="card-title">SWIFT Payout Details</span></div>
              <div className="card-body">
                <div className="gap-16">
                  {[
                    { label: 'Beneficiary Name *', key: 'beneficiaryName', placeholder: 'Enter beneficiary full legal name' },
                    { label: 'Beneficiary Bank', key: 'beneficiaryBank', placeholder: 'Enter beneficiary bank name' },
                    { label: 'Beneficiary BIC/SWIFT', key: 'beneficiaryBIC', placeholder: 'Enter BIC/SWIFT code' },
                    { label: 'Account Number / IBAN *', key: 'beneficiaryAccount', placeholder: 'Enter account number or IBAN' },
                  ].map(f => (
                    <div key={f.key} className="form-group">
                      <label className="form-label">{f.label}</label>
                      <input className="form-input" placeholder={f.placeholder} value={form[f.key]} onChange={set(f.key)} />
                    </div>
                  ))}
                  <div className="grid2">
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <select className="form-input" value={form.currency} onChange={set('currency')}>
                        <option>USD</option><option>EUR</option><option>GBP</option><option>JPY</option><option>THB</option><option>CHF</option><option>AUD</option><option>CAD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Amount *</label>
                      <input className="form-input" type="number" placeholder="0.00" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} />
                    </div>
                  </div>
                  <div className="grid2">
                    <div className="form-group">
                      <label className="form-label">Value Date</label>
                      <input className="form-input" type="date" value={form.valueDate} onChange={set('valueDate')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Purpose Code</label>
                      <select className="form-input" value={form.purposeCode} onChange={set('purposeCode')}>
                        <option value="OTHR">OTHR — Other</option>
                        <option value="TRAD">TRAD — Trade</option>
                        <option value="INVS">INVS — Investment</option>
                        <option value="SALA">SALA — Salary</option>
                        <option value="SUPP">SUPP — Supplier Payment</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Purpose of Payment</label>
                    <textarea className="form-input" placeholder="Payment description..." value={form.purposeOfPayment} onChange={set('purposeOfPayment')} />
                  </div>
                  {accounts && accounts.length > 0 && (
                    <div className="form-group">
                      <label className="form-label">Source Account</label>
                      <select className="form-input" value={sourceAccountIban} onChange={e => setSourceAccountIban(e.target.value)}>
                        <option value="">Main Wallet</option>
                        {accounts.filter(a => a.status === 'ACTIVE').map(a => (
                          <option key={a.iban} value={a.iban}>{a.ibanFormatted} — {a.holderName} — {formatUSD(a.balance)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <span style={{ fontSize: 14, color: 'var(--text2)' }}>
                      Available: <strong style={{ color: 'var(--teal)', fontSize: 16 }}>{formatUSD(availableBalance)}</strong>
                    </span>
                    <button className="btn btn-gold btn-lg" onClick={handleGoToReview}>Review POBO Payout &rarr;</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side: Architecture + History */}
          <div className="gap-16">
            <div className="card" style={{ position: 'sticky', top: 0 }}>
              <div className="card-header"><span className="card-title">POBO Architecture</span></div>
              <div className="card-body" style={{ fontSize: 12, lineHeight: 2 }}>
                <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>SWIFT sent via Issuing Bank on your behalf</div>
                {[
                  ['Debtor', ISSUING_BANK, '(sender of record)'],
                  ['Debtor Account', 'Bank Settlement Acct', ''],
                  ['Ultimate Debtor', COMPANY, '(POBO disclosure)'],
                  ['Initiating Party', 'USDW Platform', ''],
                  ['Creditor', form.beneficiaryName || '(beneficiary)', ''],
                ].map(([role, value, note]) => (
                  <div key={role} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-light)', padding: '4px 0' }}>
                    <span style={{ color: 'var(--text3)', fontWeight: 600 }}>{role}</span>
                    <span style={{ fontWeight: 500, textAlign: 'right' }}>{value} <span style={{ color: 'var(--text3)', fontSize: 10 }}>{note}</span></span>
                  </div>
                ))}
                <div style={{ marginTop: 16 }}>
                  {[
                    ['1', 'Fill SWIFT payout details'],
                    ['2', 'Platform auto-redeems USDW to fiat'],
                    ['3', 'Compliance screening (Sanctions/AML)'],
                    ['4', 'Issuing Bank sends SWIFT pacs.008'],
                    ['5', 'Beneficiary receives funds'],
                  ].map(([n, desc]) => (
                    <div key={n} style={{ display: 'flex', gap: 10, padding: '6px 0', alignItems: 'center' }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--gold-soft)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{n}</div>
                      <span style={{ fontSize: 12, color: 'var(--text2)' }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent payouts */}
            {payoutHistory.length > 0 && (
              <div className="card">
                <div className="card-header"><span className="card-title">Recent SWIFT Payouts</span></div>
                <div style={{ overflowX: 'auto' }}>
                  <table><thead><tr><th>ID</th><th>Beneficiary</th><th>Amount</th><th>Status</th></tr></thead>
                    <tbody>{payoutHistory.slice(0, 5).map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--gold)' }}>{p.payoutId}</td>
                        <td>{p.beneficiaryName}</td>
                        <td style={{ fontWeight: 600 }}>{formatUSD(p.amount)}</td>
                        <td><span className="badge badge-settled">{p.status}</span></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: REVIEW */}
      {step === 2 && (
        <div className="card" style={{ maxWidth: 740, margin: '0 auto' }}>
          <div className="card-header">
            <span className="card-title">POBO SWIFT Payout — Review & Confirm</span>
            <span className="badge badge-pending" style={{ fontSize: 11, padding: '4px 12px' }}>PENDING</span>
          </div>
          <div className="card-body">
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.7 }}>
              Funds will be sent by <strong style={{ color: 'var(--navy)' }}>{ISSUING_BANK}</strong> on your behalf via SWIFT ISO 20022. {COMPANY} is disclosed as Ultimate Debtor.
            </div>
            {reviewRows.map((r, i) => {
              if (r.section) return <div key={i} style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.2em', padding: '14px 0 6px', borderBottom: '2px solid var(--border)', marginTop: i > 0 ? 10 : 0 }}>{r.section}</div>;
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600, minWidth: 200 }}>{r.label}</span>
                  <span style={{ fontSize: r.highlight ? 16 : 13, fontWeight: r.highlight || r.gold ? 700 : 500, color: r.gold ? 'var(--gold)' : r.highlight ? 'var(--navy)' : 'var(--text)', fontFamily: r.mono || r.gold ? "'IBM Plex Mono', monospace" : 'inherit', letterSpacing: r.gold ? '0.15em' : 'normal', textAlign: 'right', maxWidth: '55%', wordBreak: 'break-all' }}>{r.value}</span>
                </div>
              );
            })}
            <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'space-between' }}>
              <button className="btn btn-outline btn-lg" onClick={() => { setAlert(null); setStep(1); }}>&larr; Edit Details</button>
              <button className="btn btn-gold btn-lg" onClick={handleSubmitPayout} disabled={sending}>
                {sending ? 'Processing...' : 'Confirm POBO SWIFT Payout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: RESULT */}
      {step === 3 && (
        <div className="gap-16">
          <div className="card" style={{ textAlign: 'center' }}>
            <div className="card-body" style={{ padding: 40 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: result?.success ? 'linear-gradient(135deg, var(--teal), var(--teal-light))' : 'linear-gradient(135deg, var(--coral), #FF9999)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 32, color: 'white' }}>
                {result?.success ? '\u2713' : '\u2717'}
              </div>
              <h3 style={{ fontFamily: "'Source Serif 4', serif", fontSize: 24, color: 'var(--navy)', marginBottom: 8 }}>
                {result?.success ? 'POBO SWIFT Payout Settled' : 'Payout Failed'}
              </h3>
              {result?.payout && (
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                  UETR: <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: 'var(--gold)' }}>{result.payout.uetr}</span>
                </div>
              )}
            </div>
          </div>

          {/* Dwin SWIFT Gateway Pipeline */}
          {result?.pipeline && (
            <div className="card">
              <div className="card-header"><span className="card-title">Dwin SWIFT Gateway — Processing Pipeline</span></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table>
                  <thead><tr><th style={{ width: 40 }}>#</th><th>Step</th><th>Timestamp</th><th>Detail</th><th style={{ width: 50, textAlign: 'center' }}>Status</th></tr></thead>
                  <tbody>
                    {result.pipeline.map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 700, color: 'var(--gold)' }}>{i + 1}</td>
                        <td style={{ fontWeight: 600, fontSize: 12 }}>{p.step}</td>
                        <td style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace" }}>{p.timestamp?.slice(11, 19)}</td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.detail}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,148,136,0.1)', color: 'var(--teal)', fontSize: 13 }}>{'\u2713'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Compliance Checks */}
          {result?.complianceChecks && (
            <div className="card">
              <div className="card-header"><span className="card-title">Compliance Screening Results</span></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table>
                  <thead><tr><th>Check</th><th>Target/Detail</th><th>Result</th></tr></thead>
                  <tbody>
                    {result.complianceChecks.map((c, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, fontSize: 12 }}>{c.list || c.check}</td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{c.target || c.note || `Amount: ${c.amount || '-'}`}</td>
                        <td><span className={`badge ${c.result === 'CLEAR' || c.result === 'UNIQUE' || c.result === 'WITHIN_LIMIT' ? 'badge-settled' : 'badge-pending'}`}>{c.result}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result?.payout && (
            <div className="grid2">
              <div className="card">
                <div className="card-header"><span className="card-title">Settlement Record</span></div>
                <div className="card-body">
                  {[
                    ['Payout ID', result.payout.payoutId],
                    ['Status', result.payout.status],
                    ['UETR', result.payout.uetr],
                    ['SWIFT Message ID', result.payout.swiftMessageId],
                    ['Digital Signature', result.signature ? result.signature.substring(0, 24) + '...' : '-'],
                    ['Amount', formatUSD(result.payout.amount)],
                    ['Beneficiary', result.payout.beneficiaryName],
                    ['Settled At', result.payout.settledAt ? formatDate(result.payout.settledAt) : '-'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)' }}>{k}</span>
                      <span style={{ fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card">
                <div className="card-header"><span className="card-title">POBO Party Roles</span></div>
                <div className="card-body">
                  {result.poboPartyRoles && Object.entries(result.poboPartyRoles).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text3)', fontWeight: 600 }}>{k}</span>
                      <span style={{ fontWeight: 500 }}>{v}</span>
                    </div>
                  ))}
                  {result.treasuryApproval && (
                    <div style={{ marginTop: 12, padding: 12, background: 'var(--gold-soft)', borderRadius: 8, fontSize: 12 }}>
                      <strong>Treasury:</strong> {result.treasuryApproval.note}<br />
                      <strong>Approver:</strong> {result.treasuryApproval.approver}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {isoMessage && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">ISO 20022 pacs.008 — POBO Message</span>
                <button className="btn btn-outline btn-sm" onClick={() => navigator.clipboard.writeText(isoMessage)}>Copy XML</button>
              </div>
              <div className="card-body">
                <div className="code-block" style={{ maxHeight: 400 }}>{isoMessage}</div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn btn-gold btn-lg" onClick={handleNew}>New Payout</button>
            <button className="btn btn-outline btn-lg" onClick={() => window.print()}>Print Receipt</button>
          </div>
        </div>
      )}
    </div>
  );
}
